import React from 'react';
import { ArrowUp, ArrowDown, MoveHorizontal } from "lucide-react";

export function normalizeRows(rows: any[], dateKeys = ['date', 'Date', 'RecordDate']): any[] {
  return rows.map(row => ({
    ...row,
    date: dateKeys.map(k => row[k]).find(Boolean) || ''
  }));
}

export function getSortedDates(rows: any[]): any[] {
  return [...rows].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
}

export function hasCommentaryData(row: any): boolean {
  const commentaryFieldNames = [
    'pe_inv_cum_gains_draws',
    'pe_prod_sales',
    'hdpe',
    'ldpe',
    'lldpe',
    'pvc_inv_cum_gains_draws',
    'pvc_prod_sales',
    'pvc_exports',
    'pvc_domestic_sales',
    'pp_supply_demand',
    'ps_inv_cum_gains_draws',
    'ps_supply_demand',
    'ps_imports',
    'sm_styrene',
    'bz_benzene',
    'bd_butadiene'
  ];
  
  return commentaryFieldNames.some(key => {
    if (!(key in row)) return false;
    const value = row[key];
    return value !== null && value !== undefined && typeof value === 'string' && value.trim().length > 5;
  });
}

export function getAvailableDates(sortedRows: any[], months = 25, prelimData: any[] = []): string[] {
  const sliced = sortedRows.slice(0, months);

  if (sliced.length === 0) return [];

  const mostRecentRow = sliced[0];
  const hasPrelim = prelimData.some(p => p.date === mostRecentRow.date);
  const prelimRow = prelimData.find(p => p.date === mostRecentRow.date);
  const hasCommentary = prelimRow ? hasCommentaryData(prelimRow) : false;

  if (!hasPrelim || !hasCommentary) {
    return sliced.slice(1).map(r => r.date);
  }

  return sliced.map(r => r.date);
}

export function getFirstDateWithPrelimCommentary(prelimData: any[], months = 25): string | null {
  if (!prelimData || prelimData.length === 0) return null;

  const sorted = prelimData.slice(0, months).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  for (const row of sorted) {
    if (hasCommentaryData(row)) {
      return row.date;
    }
  }

  return null;
}

export function shouldShowFinalByDefault(date: string, mostRecentDate: string, finalData: any[], prelimData: any[]): boolean {
  if (date !== mostRecentDate) {
    return true;
  }

  const prelimRow = prelimData.find(p => p.date === date);
  const finalRow = finalData.find(f => f.date === date);

  const prelimHasCommentary = prelimRow && hasCommentaryData(prelimRow);
  const finalHasCommentary = finalRow && hasCommentaryData(finalRow);

  return prelimHasCommentary && finalHasCommentary;
}

export function formatDateLabel(dateStr: string): string {
  const [year, month, day] = dateStr.split('-').map(Number);
  const isoString = `${year.toString().padStart(4, '0')}-${month
    .toString()
    .padStart(2, '0')}-${day.toString().padStart(2, '0')}T00:00:00Z`;
  const date = new Date(isoString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    timeZone: 'UTC'
  });
}

export function formatCommentary(text: string): string {
  if (!text) return '';
  return text
    .replace(/\n\n|\r\n\r\n|\r\r/g, '<br><br>')
    .replace(/\r\n|\n|\r|\x0b/g, '<br>')
    .replace(/\r\?/g, '<br>-')
    .replace(/\?/g, '-');
}

export function getChange(current: number | undefined, previous: number | undefined, isPercent = false, divisor = 1, decimal = 0): string {
  if (!current || !previous || current === previous) return 'Flat';
  const diff = (current - previous) / divisor;
  const formatted = decimal ? diff.toFixed(decimal).toLocaleString() : Math.round(diff).toLocaleString();
  const sign = diff > 0 ? '+' : '';
  return isPercent ? `${sign}${formatted}%` : `${sign}${formatted}`;
}

export function stripSigns(value: string): string {
  return value ? value.replace(/[-+]/g, '') : value;
}

export function renderStatCard(
  title: string,
  value: string | number,
  change: string,
  unit?: string,
  previous?: string,
  annualChange?: string
) {
  const isNegative = change.includes('-');
  const isPositive = change.includes('+');
  const isAnnualNegative = annualChange?.includes('-');
  const isAnnualPositive = annualChange?.includes('+');

  return (
    <div data-testid="stat-card" className={`p-5 bg-rs-off-black rounded-lg overflow-auto ${!title.startsWith('PE') && title.includes('PE') ? 'ml-15' : ''}`}>
      <div data-testid="stat-title" className="text-sm">{title}</div>
      <div data-testid="stat-value" className="text-2xl font-medium flex items-center">
        {value}{unit}
        <span className={`ml-1 flex items-center ${
          isNegative ? 'text-rs-red' : isPositive ? 'text-rs-dark-green' : ''
        }`}>
            {isNegative ? (
              <ArrowDown className="w-5 " />
            ) : isPositive ? (
              <ArrowUp className="w-5" />
            ) :  <MoveHorizontal className=" mx-1 w-5" /> }
          <span>{stripSigns(change.toLowerCase())}</span>
        </span>
        {annualChange && (
          <>
            <span className="ml-3">YOY:</span>
            <span className={`ml-1 flex items-center ${
              isAnnualNegative ? 'text-rs-red' : isAnnualPositive ? 'text-rs-dark-green' : ''
            }`}>
              {isAnnualNegative ? (
                <ArrowDown className="w-5" />
              ) : isAnnualPositive ? (
                <ArrowUp className="w-5" />
              ) : <MoveHorizontal className="w-5" />}
              <span>{stripSigns(annualChange.toLowerCase())}</span>
            </span>
          </>
        )}
        {previous && <span className="ml-1 text-sm">{previous}</span>}
      </div>
    </div>
  );
}
